/**
 * How wide the detail pane is, remembered per user.
 *
 * Christopher, 2026-08-07, on the split: "like in kendo, you have the ability to
 * still drag the split if needed. that grid needs to place nicely."
 *
 * Same store and the same reasoning as `useColumnWidths` — `core.user_setting`
 * via `my_settings()` / `set_user_setting()`, no migration, because a per-user
 * key/value store is what it is for. One key for the whole console rather than
 * one per entity: the divider is a property of how THIS PERSON works, not of
 * what they are looking at, and a width that resets every time you change entity
 * is a width nobody bothers to set.
 *
 * Stored as a PERCENTAGE, not pixels. A pixel width set on a 2560px monitor is
 * a detail pane wider than a laptop screen; a percentage survives the move.
 */
import { useCallback, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DEFAULT_INSPECTOR_PERCENT, MAX_INSPECTOR_PERCENT, MIN_INSPECTOR_PERCENT,
} from '@solum/ui'
import { useSolum } from '../data/client'

const KEY = 'split.width'
/** The grid keeps at least this much, the detail at least this much. */
const MIN_PCT = MIN_INSPECTOR_PERCENT
const MAX_PCT = MAX_INSPECTOR_PERCENT
const DEFAULT_PCT = DEFAULT_INSPECTOR_PERCENT

export interface SplitWidth {
  /** detail pane width, as a percentage of the split container */
  pct: number
  /** live drag — not persisted until `commit` */
  preview: (pct: number) => void
  commit: (pct: number) => void
  reset: () => void
  /** the settings read has settled; before that `pct` is the default */
  ready: boolean
}

const clamp = (n: number) => Math.min(MAX_PCT, Math.max(MIN_PCT, Math.round(n)))

export function useSplitWidth(): SplitWidth {
  const { client } = useSolum()
  const qc = useQueryClient()
  const [live, setLive] = useState<number | null>(null)
  const saving = useRef<number | null>(null)

  const q = useQuery({
    queryKey: ['user-settings'],
    queryFn: () => client.raw().rpcScalar<Record<string, unknown>>('my_settings'),
    staleTime: 60_000,
  })
  // A REFUSED settings read is settled, not pending: there is no stored width,
  // the split opens at the default, and that is a degraded preference rather
  // than a false statement (the classification `useColumnWidths` establishes).
  const settled = !q.isLoading || q.isError

  const stored = useMemo(() => {
    const v = Number((q.data?.[KEY] as number | undefined) ?? NaN)
    return Number.isFinite(v) ? clamp(v) : DEFAULT_PCT
  }, [q.data])

  const save = useMutation({
    mutationFn: (pct: number) => client.raw().rpcScalar('set_user_setting', { p_key: KEY, p_value: pct }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['user-settings'] }) },
  })

  const commit = useCallback((pct: number) => {
    const next = clamp(pct)
    setLive(next)
    // Skip a write that changes nothing — a drag that ends where it started is
    // the commonest gesture of all (grab, think, let go).
    if (saving.current === next || (q.isSuccess && stored === next)) return
    saving.current = next
    save.mutate(next)
  }, [q.isSuccess, stored, save])

  return {
    pct: live ?? stored,
    preview: (pct) => setLive(clamp(pct)),
    commit,
    reset: () => { setLive(null); saving.current = DEFAULT_PCT; save.mutate(DEFAULT_PCT) },
    ready: settled,
  }
}
